import { createSelector } from '@reduxjs/toolkit';
import { getMovementsResultsSelector } from '../state-manager/selectors/treatmentPlansSelectors';
import { ImprovementDirections, MovementResultsDto } from '../types/backendType';
import { getCurrentPatientIdSelector } from '../state-manager/selectors/appSelectors';
import { ActivityDefinition, ActivityDefinitionId } from '../types/library';
import { getAllActivityDefinitionsSelector } from '../state-manager/selectors/customActivitiesSelectors';
import { TreatmentPlan } from './TreatmentPlan';
import { PostMetric } from './PostMetric';
import { getPatientTreatmentPlansSelector } from './factories/treatmentPlanFactories';
import { isEmpty } from '../state-manager/utils/compare';

export function isMetricTwoSided(metricKey: string) {
  return metricKey.includes('left') || metricKey.includes('right');
}
export function stripTwoSidedMetricKeyOfSide(metricKey: string) {
  const splitKey = metricKey.split('_');
  return splitKey.slice(0, splitKey.length - 1).join('_');
}

export function normalizeMetricKey(metricKey: string) {
  return isMetricTwoSided(metricKey) ? stripTwoSidedMetricKeyOfSide(metricKey) : metricKey;
}

export type Result = {
  key: string;
  name: string;
  unit: string;
  value: number;
  goal?: number | undefined;
  metGoal?: boolean | undefined;
  improvementDirection?: ImprovementDirections | undefined;
  side?: string | undefined;
  math?: string | undefined;
  standardDeviation?: number | undefined;
  automationLevel?: number | undefined;
  minClinicalSignificance?: number | undefined;
  std?: number;
  mean?: number;
  median?: number;
};

export class ActivityExecution {
  private movementResultsDto: MovementResultsDto;

  activityDefinition: ActivityDefinition | undefined;

  treatmentPlan: TreatmentPlan;

  id: string;

  activityDefinitionId: string;

  treatmentPlanId: string;

  sessionId: string;

  subSessionId: string;

  status: string;

  statusDescription: string;

  restartedReason: 'PatientRequested' | 'PlanUpdated' | undefined;

  restartedNote: string | undefined;

  stopDate: Date;

  startDate: Date;

  activityIndexInPlan: number;

  constructor({
    movementResultsDto,
    activityDefinition,
    treatmentPlan,
  }: {
    movementResultsDto: MovementResultsDto;
    activityDefinition?: ActivityDefinition;
    treatmentPlan: TreatmentPlan;
  }) {
    this.movementResultsDto = movementResultsDto;
    this.activityDefinition = activityDefinition;
    this.treatmentPlan = treatmentPlan;
    this.activityDefinitionId = this.movementResultsDto.movement_id;
    this.treatmentPlanId = this.movementResultsDto.treatment_plan_id;
    this.sessionId = this.movementResultsDto.session_id;
    this.subSessionId = this.movementResultsDto.sub_session_id;
    this.status = this.movementResultsDto.status;
    this.statusDescription = this.movementResultsDto.status_description;
    this.restartedReason = this.movementResultsDto.restarted_reason;
    this.restartedNote = this.movementResultsDto.restarted_note;
    this.stopDate = new Date(this.movementResultsDto.stop_date);
    this.startDate = new Date(this.movementResultsDto.start_date);
    this.id = `${this.sessionId}-${this.subSessionId}`;
    // Kept for anything still reading a positional index; the lookup below no longer uses it.
    this.activityIndexInPlan = this.movementResultsDto.plan_index - 1;
  }

  get activity() {
    // Match on the SLOT, in this order: slot_id, then plan_index, then list position.
    //
    // slot_id first because it is the only one that survives an edit. plan_index is a position
    // now and is rewritten on every save, so after a clinician reorders the plan, a result
    // recorded at position 3 belongs to whatever is at position 3 TODAY — which is a different
    // exercise. That silently shows this run's goals and band/volume data against the wrong
    // exercise on the Session view, with nothing to indicate it.
    //
    // The two fallbacks are for runs and plans recorded before slot identity existed. Each one
    // is strictly weaker than the one above it, so they are only reached when nothing better is
    // available.
    const bySlotId =
      this.movementResultsDto.slot_id === undefined
        ? undefined
        : this.treatmentPlan?.activities.find(
            (candidate) => candidate.slotId === this.movementResultsDto.slot_id,
          );
    const bySlotNumber = this.treatmentPlan?.activities.find(
      (candidate) => candidate.planIndex === this.movementResultsDto.plan_index,
    );
    return (
      bySlotId ?? bySlotNumber ?? this.treatmentPlan?.activities[this.activityIndexInPlan]
    );
  }

  get goals() {
    return this.activity?.goals;
  }

  get name() {
    return this.activityDefinition?.name;
  }

  getGoal(metricKey: string) {
    return this.goals?.[metricKey];
  }

  getMetric(key: string) {
    const normalizedKey = normalizeMetricKey(key);
    if (this.activityDefinition) {
      try {
        return new PostMetric({
          activityDefinition: this.activityDefinition,
          key: normalizedKey,
        });
      } catch (e) {
        return undefined;
      }
    }
  }

  get type() {
    return this.activity?.type?.[0];
  }

  get sets() {
    return this.movementResultsDto?.results_data?.summary?.num_of_sets;
  }

  get reps() {
    return this.movementResultsDto?.results_data?.sets_info?.map(({ reps }) => reps);
  }

  get hold() {
    return this.movementResultsDto?.results_data?.sets_info?.map(({ hold }) => hold);
  }

  get duration() {
    return this.movementResultsDto.results_data?.sets_info?.map(({ duration }) => duration);
  }

  // Boolean array aligned with reps / hold / duration, marking per-set entries the algo emitted
  // as in-progress snapshots (is_partial=true). Used by the session detail view to render the
  // value with a "(partial)" suffix and subdued styling.
  get setsPartial() {
    return this.movementResultsDto.results_data?.sets_info?.map(
      ({ is_partial }) => is_partial === true,
    );
  }

  get compliance() {
    return this.movementResultsDto.movement_completion_rate;
  }

  get rpeSurvey() {
    return this.movementResultsDto.results_data?.rpe_survey;
  }

  /** 'local' when the tablet ran the algo for this movement; every other row ran in the cloud. */
  get algoMode(): 'local' | 'cloud' {
    return this.movementResultsDto?.results_data?.algo_mode === 'local' ? 'local' : 'cloud';
  }

  get transcriptionComment() {
    return this.movementResultsDto.results_data?.transcription_comment;
  }

  get automationLevel() {
    let automationLevel = this.movementResultsDto.results_data?.summary?.phase_eng[0];
    if (isEmpty(automationLevel)) {
      automationLevel = this.activityDefinition?.automationLevel;
    }
    return automationLevel;
  }

  get results() {
    const mapper = ({
      key,
      value,
      unit,
      side,
      standardDeviation,
      median,
      mean,
      std,
    }: {
      key: string;
      value: number;
      unit: string;
      side: string | undefined;
      standardDeviation: number | undefined | 'NA';
      median?: number;
      mean?: number;
      std?: number;
    }) => {
      const goal = this.goals?.[key];
      const metric = this.getMetric(key);
      const minClinicalSignificance = metric?.minClinicalSignificance;

      let metGoal: undefined | boolean = undefined;
      if (goal !== undefined) {
        if (metric?.improvementDirection === ImprovementDirections.Up) {
          metGoal = goal <= value;
        }
        if (metric?.improvementDirection === ImprovementDirections.Down) {
          metGoal = goal > value;
        }
        if (metric?.improvementDirection === ImprovementDirections.Goal) {
          metGoal =
            value <= goal + (minClinicalSignificance ?? 0) &&
            value >= goal - (minClinicalSignificance ?? 0);
        }
      }
      const improvementDirection = metric?.improvementDirection;
      const math = metric?.math;
      const libraryStandardDeviation = metric?.libraryStandardDeviation;
      const automationLevel = metric?.automationLevel;

      const getStandardDeviation = () => {
        if (standardDeviation && standardDeviation !== 'NA') {
          return standardDeviation;
        } else if (libraryStandardDeviation && libraryStandardDeviation !== 'NA') {
          return libraryStandardDeviation;
        } else {
          return undefined;
        }
      };

      return {
        key,
        name: metric?.name ?? key,
        unit,
        value,
        goal,
        metGoal,
        improvementDirection,
        side: side == 'NA' ? undefined : side,
        math: math === 'NA' ? undefined : math,
        standardDeviation: getStandardDeviation(),
        automationLevel: automationLevel,
        minClinicalSignificance,
        median,
        mean,
        std,
      };
    };
    const newResults: Result[] = [];
    this.movementResultsDto.results_data?.summary?.post_metrics_array?.forEach((result) => {
      const {
        metric_data: { side, unit, value, metric_sd: standardDeviation },
        metric_name: key,
      } = result;

      const median = result.metric_stats?.median;
      const mean = result.metric_stats?.mean;
      const std = result.metric_stats?.std;

      if (!isEmpty(value)) {
        newResults.push(
          mapper({ key, value, unit, side, standardDeviation, median, mean, std }),
        );
      }
    });

    const rpeSurvey = this.rpeSurvey;
    if (this.status === 'RpeSurvey' && rpeSurvey) {
      const { rpe, pain } = rpeSurvey;
      if (rpe) {
        const rpeNumber = Number(rpe);
        newResults.push({
          key: 'rpe',
          value: rpeNumber,
          name: 'RPE',
          unit: '#',
        });
      }
      if (pain) {
        const painNumber = Number(pain);
        newResults.push({
          key: 'pain',
          value: painNumber,
          name: 'Pain',
          unit: '#',
        });
      }
    }

    if (newResults) {
      return newResults.filter((result) => {
        return result.automationLevel !== 0;
      });
    }

    return [];
  }
}

export const getPatientActivitiesExecutionsSelector = createSelector(
  [
    getMovementsResultsSelector,
    getCurrentPatientIdSelector,
    getAllActivityDefinitionsSelector,
    getPatientTreatmentPlansSelector,
  ],
  (
    movementsResults,
    patientId,
    activityDefinitions,
    patientTreatmentPlans,
  ): ActivityExecution[] | undefined => {
    if (!patientId) {
      return;
    }
    const activityExecutions: ActivityExecution[] = [];
    movementsResults[patientId]
      ?.filter((movementResultsDto) => {
        return movementResultsDto.movement_details?.movement_info?.supported !== 'false';
      })
      .forEach((movementResultsDto) => {
        const treatmentPlan = patientTreatmentPlans?.find(({ id }) => {
          return id === movementResultsDto.treatment_plan_id;
        });
        if (!treatmentPlan) {
          return;
        }
        const activityDefinition = activityDefinitions?.[movementResultsDto.movement_id];
        activityExecutions.push(
          new ActivityExecution({
            movementResultsDto,
            activityDefinition,
            treatmentPlan,
          }),
        );
      });
    return activityExecutions;
  },
);

export function groupActivitiesExecutionsByActivityDefinition(
  activitiesExecutions: ActivityExecution[] | undefined,
): Record<ActivityDefinitionId, ActivityExecution[]> | undefined {
  const activitiesExecutionsMap: Record<ActivityDefinitionId, ActivityExecution[]> = {};
  if (!activitiesExecutions) {
    return;
  }
  activitiesExecutions?.forEach((activityExecution) => {
    if (!activitiesExecutionsMap[activityExecution.activityDefinitionId]) {
      activitiesExecutionsMap[activityExecution.activityDefinitionId] = [];
    }
    activitiesExecutionsMap[activityExecution.activityDefinitionId]?.push(activityExecution);
  });
  return activitiesExecutionsMap;
}
